import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { Models } from "@/api/types";
import { useUser, useUserPreferences } from "modules/user/useUser/useUser";

import { AdonisIdentity } from "../adonis.types";
import AdonisChat from "../AdonisChat/AdonisChat";

const sessionKey = "adonis_chat_session_id";
const sessionDurationInMs = 60 * 60 * 1000; // 1 hour

export interface AdonisSession {
  id: string;
  expiresAt: number;
}

const createAdonisSession = (): AdonisSession => {
  const id = uuidv4();
  const expiresAt = Date.now() + sessionDurationInMs;
  const session = { id, expiresAt };
  localStorage.setItem(sessionKey, JSON.stringify(session));
  return session;
};

const loadSession = (): AdonisSession => {
  try {
    const stored = localStorage.getItem(sessionKey);
    if (stored) {
      const session = JSON.parse(stored) as AdonisSession;
      if (Date.now() < session.expiresAt) {
        return session;
      }
    }
  } catch (err) {
    console.warn("Invalid session, recreating...");
  }

  return createAdonisSession();
};

interface AdonisChatContextProps {
  isEnabled: boolean;
  isPersonalisationEnabled: boolean;
  openChat: () => void;
  closeChat: () => void;
  isOpen: boolean;
  hasMessages: boolean;
  setHasMessages: (hasMessages: boolean) => void;
  session: AdonisSession;
  resetSession: () => void;
  showCTABanner: boolean;
  setShowCTABanner: (show: boolean) => void;
  persona: AdonisIdentity;
  pendingMessage: string | null;
  openChatWithMessage: (message: string) => void;
  clearPendingMessage: () => void;
  platformLocation?: {
    bookAppointmentUrl?: string | null;
    phoneNumber?: string | null;
    email?: string | null;
  };
}

export const AdonisChatContext = createContext<AdonisChatContextProps>(
  {} as AdonisChatContextProps
);

export const useAdonisChat = () => {
  const ctx = useContext(AdonisChatContext);
  if (!ctx) {
    throw new Error(`AdonisChatContext.Provider not found.`);
  }
  return ctx;
};

export const AdonisChatProvider = ({
  children,
  platformSettings,
}: {
  children: ReactNode;
  platformSettings?: Models<"PlatformSettingModel1"> | null;
}) => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [hasMessages, setHasMessages] = useState(false);
  const [session, setSession] = useState<AdonisSession>(loadSession());
  const [showCTABanner, setShowCTABanner] = useState(true);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);

  const { user } = useUser();
  const { preferences } = useUserPreferences();

  // Compute persona locally from user profile (gender), falling back to default
  const persona = useMemo<AdonisIdentity>(() => {
    const preferencesPersona = preferences?.aiPersona;
    if (preferencesPersona) {
      return {
        name: preferencesPersona,
        pronoun: preferencesPersona === "Aria" ? "she" : "he",
        objectPronoun: preferencesPersona === "Aria" ? "her" : "him",
      };
    }

    const gender = user?.gender?.toLowerCase();
    if (gender === "female") {
      return { name: "Aria", pronoun: "she", objectPronoun: "her" };
    }
    return { name: "Adonis", pronoun: "he", objectPronoun: "him" };
  }, [preferences?.aiPersona, user?.gender]);

  // Extract platform location data for button identification
  const platformLocation = useMemo(
    () =>
      user?.platformLocation
        ? {
            bookAppointmentUrl: user.platformLocation.bookAppointmentUrl,
            phoneNumber: user.platformLocation.phoneNumber,
            email: user.platformLocation.email,
          }
        : undefined,
    [user?.platformLocation]
  );

  const clearPendingMessage = () => {
    setPendingMessage(null);
  };

  const resetSession = () => {
    const newSession = createAdonisSession();
    setSession(newSession);
    setPendingMessage(null);
  };

  useEffect(() => {
    const now = Date.now();
    const timeUntilExpiry = session.expiresAt - now;

    if (timeUntilExpiry <= 0) {
      const newSession = createAdonisSession();
      setSession(newSession);
      return;
    }

    const timeout = setTimeout(() => {
      const newSession = createAdonisSession();
      setSession(newSession);
    }, timeUntilExpiry);

    return () => clearTimeout(timeout);
  }, [session.expiresAt]);

  const openChat = () => {
    setIsOpen(true);
    queryClient.invalidateQueries({
      queryKey: ["recommendations-history", session.id],
    });
  };

  const openChatWithMessage = (message: string) => {
    setPendingMessage(message);
    openChat();
  };

  const closeChat = () => {
    // @ts-expect-error - pdpNavigate is not defined in the window object
    window.pdpNavigate = undefined;
    setIsOpen(false);
    setPendingMessage(null);
  };

  useEffect(() => {
    if (isOpen) {
      setIsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <AdonisChatContext.Provider
      value={{
        isEnabled:
          (user && !user.isGuest && platformSettings?.aiAdvisorsEnabled) ??
          false,
        isPersonalisationEnabled:
          (user &&
            !user.isGuest &&
            platformSettings?.patientPersonalisationEnabled) ??
          false,
        openChat,
        closeChat,
        isOpen,
        hasMessages,
        setHasMessages,
        session,
        resetSession,
        showCTABanner,
        setShowCTABanner,
        persona,
        pendingMessage,
        openChatWithMessage,
        clearPendingMessage,
        platformLocation,
      }}
    >
      {children}
      {user && !user.isGuest && (
        <AdonisChat
          isOpen={isOpen}
          onClose={closeChat}
          session={session}
          resetSession={resetSession}
        />
      )}
    </AdonisChatContext.Provider>
  );
};
